import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message } from "@mimber/ui-web"
import { UserOutlined, MailOutlined, ClockCircleOutlined } from "@mimber/ui-web"
import { useMembers } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { OrganizationMembershipDto } from "@mimber/schema"
import type { MembersTableProps } from "./MembersTable.types"
import {
  MEMBERSHIP_STATUS_CONFIG,
  getMembershipStatusDisplay,
} from "@/utils/membership"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const MembersTable: React.FunctionComponent<MembersTableProps> = ({
  onRowClick,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "members",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useMembers(queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load members: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<OrganizationMembershipDto> = useMemo(
    () => [
      {
        title: "Member",
        key: "member",
        render: (_, record) => (
          <Space>
            <UserOutlined />
            <div>
              <div>
                <Text strong>
                  {record.user.firstName && record.user.lastName
                    ? `${record.user.firstName} ${record.user.lastName}`
                    : record.user.email}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {record.user.email}
                </Text>
              </div>
            </div>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Email",
        key: "email",
        // Redundant with the email shown under the member's name — hide until there's room
        responsive: ["md"],
        render: (_, record) => (
          <Space>
            <MailOutlined />
            <Text>{record.user.email}</Text>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search email"),
      },
      {
        title: "Joined",
        dataIndex: "joinedAt",
        key: "joinedAt",
        responsive: ["md"],
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (joinedAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text>{formatTableDate(joinedAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        responsive: ["sm"],
        render: (status?: OrganizationMembershipDto["status"]) => {
          const config = getMembershipStatusDisplay(status)
          return <Tag color={config.color}>{config.label}</Tag>
        },
        filters: Object.entries(MEMBERSHIP_STATUS_CONFIG).map(
          ([value, { label }]) => ({
            text: label,
            value,
          })
        ),
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        responsive: ["lg"],
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Text type="secondary">{formatTableDate(createdAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const handleRowClick = (record: OrganizationMembershipDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<OrganizationMembershipDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    // Convert antd types to our utility function types
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<OrganizationMembershipDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
